/**
 * First-touch marketing attribution.
 *
 * Captures ad parameters on the visitor's first landing, keeps them for a
 * limited window, and writes them onto the Shopify cart as cart attributes so
 * they land on the order in admin.
 *
 * Deliberately does NOT rewrite same-origin links: putting utm_* on internal
 * URLs restarts the GA4 campaign/session on every click and creates duplicate
 * crawlable URLs. Storage is shared across the whole origin, so decoration is
 * only needed for links leaving to a sister domain.
 */
(function () {
  var CFG = window.BDB_ATTRIBUTION || {};
  if (CFG.enabled === false) return;

  var KEY = 'bdb_first_touch_params';
  var PARAM_KEYS = CFG.params || [];
  var CROSS_DOMAIN_HOSTS = CFG.crossDomainHosts || [];
  var TTL_MS = (CFG.ttlDays || 30) * 24 * 60 * 60 * 1000;

  if (!PARAM_KEYS.length) return;

  /* ---------- storage ---------- */

  function readRaw() {
    try {
      var ls = localStorage.getItem(KEY);
      if (ls) return ls;
    } catch (e) {}
    try {
      var prefix = KEY + '=';
      var jar = document.cookie.split(';');
      for (var i = 0; i < jar.length; i++) {
        var c = jar[i].trim();
        if (c.indexOf(prefix) === 0) return decodeURIComponent(c.substring(prefix.length));
      }
    } catch (e) {}
    return null;
  }

  function save(record) {
    var payload = JSON.stringify(record);
    try {
      localStorage.setItem(KEY, payload);
    } catch (e) {}
    try {
      document.cookie =
        KEY +
        '=' +
        encodeURIComponent(payload) +
        '; path=/; max-age=' +
        Math.floor(TTL_MS / 1000) +
        '; SameSite=Lax' +
        (location.protocol === 'https:' ? '; Secure' : '');
    } catch (e) {}
  }

  function clear() {
    try {
      localStorage.removeItem(KEY);
    } catch (e) {}
    try {
      document.cookie = KEY + '=; path=/; max-age=0';
    } catch (e) {}
  }

  // Returns the stored first touch, or null when absent or expired.
  function load() {
    var raw = readRaw();
    if (!raw) return null;

    var record;
    try {
      record = JSON.parse(raw);
    } catch (e) {
      clear();
      return null;
    }
    if (!record || typeof record !== 'object') return null;

    // Records written before this script had a timestamp: adopt them once, aged
    // from now, so in-flight visitors don't lose their attribution.
    if (!record.ts || !record.params) {
      if (!Object.keys(record).length) return null;
      record = { params: record, ts: Date.now() };
      save(record);
      return record;
    }

    if (Date.now() - record.ts > TTL_MS) {
      clear();
      return null;
    }
    return record;
  }

  /* ---------- capture ---------- */

  function paramsFromUrl() {
    var search = new URLSearchParams(location.search);
    var found = {};
    PARAM_KEYS.forEach(function (key) {
      var value = search.get(key);
      if (value) found[key] = value.slice(0, 255);
    });
    return found;
  }

  var firstTouch = load();
  var current = paramsFromUrl();

  if (!firstTouch && Object.keys(current).length) {
    var referrer = '';
    try {
      if (document.referrer && new URL(document.referrer).hostname !== location.hostname) {
        referrer = document.referrer.slice(0, 255);
      }
    } catch (e) {}

    current.landing_page = (location.pathname + location.search).slice(0, 255);
    if (referrer) current.referrer = referrer;

    firstTouch = { params: current, ts: Date.now() };
    save(firstTouch);
  }

  /* ---------- cart attributes ---------- */

  // The only reliable way to get attribution onto a Shopify order: /cart/add
  // silently drops unknown fields, so bare utm_* hidden inputs never arrive.
  var cartWritten = false;

  function writeCartAttributes() {
    if (cartWritten || !firstTouch || CFG.writeCartAttributes === false) return;
    if (CFG.cartHasAttribution) return;

    cartWritten = true;

    var attributes = {};
    Object.keys(firstTouch.params).forEach(function (key) {
      attributes[key] = firstTouch.params[key];
    });
    attributes.first_touch_at = new Date(firstTouch.ts).toISOString();

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    }).catch(function () {
      cartWritten = false;
    });
  }

  if (CFG.cartItemCount > 0) writeCartAttributes();
  document.addEventListener('product-ajax:added', writeCartAttributes);
  document.addEventListener('cart:updated', writeCartAttributes);

  /* ---------- cross-domain hand-off ---------- */

  // localStorage/cookies don't cross origins, so a link to a sister domain is
  // the one place the parameters have to ride along in the URL. Rewritten at
  // click time only, so no decorated URL is ever exposed to a crawler.
  function decorate(event) {
    if (!firstTouch || !CROSS_DOMAIN_HOSTS.length) return;

    var link = event.target.closest && event.target.closest('a[href]');
    if (!link) return;

    var url;
    try {
      url = new URL(link.getAttribute('href'), location.origin);
    } catch (e) {
      return;
    }
    if (url.hostname === location.hostname) return;
    if (CROSS_DOMAIN_HOSTS.indexOf(url.hostname) === -1) return;

    PARAM_KEYS.forEach(function (key) {
      if (firstTouch.params[key] && !url.searchParams.has(key)) {
        url.searchParams.set(key, firstTouch.params[key]);
      }
    });
    link.setAttribute('href', url.toString());
  }

  document.addEventListener('click', decorate, true);
  document.addEventListener('auxclick', decorate, true);

  /* ---------- public read API (GTM / GA4 / apps) ---------- */

  window.BdbAttribution = {
    get: function () {
      return firstTouch ? JSON.parse(JSON.stringify(firstTouch.params)) : null;
    },
    capturedAt: function () {
      return firstTouch ? new Date(firstTouch.ts) : null;
    },
    clear: clear
  };

  if (CFG.pushToDataLayer && firstTouch) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'first_touch_attribution',
      first_touch: firstTouch.params,
      first_touch_at: new Date(firstTouch.ts).toISOString()
    });
  }
})();
